import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { requireLiveSession } from "@/lib/requireLiveSession";

/** React Query key prefix; AcceptInvitePage seeds and invalidates it. */
export const CURRENT_ORG_MEMBERSHIP_QUERY_KEY = "current-org-membership";

/**
 * Whether the signed-in user holds a CURRENT row in `organization_members` —
 * i.e. is organization staff (super_admin, admin or editor) right now.
 *
 * "Current" means `is_current = true`, exactly: the reading every
 * `.eq("is_current", true)` in the app uses (useOrgMemberCount, the editor
 * presence roster, postLoginDestination) and the one `is_org_content_editor`
 * enforces since 20260913020000. A departed member's retained row has
 * `is_current = false`; a NULL counts as not current, like SQL equality.
 * `end_date` is not consulted here. The database is not uniform: the
 * organizations read policies (#952), `private.is_current_org_admin` and
 * `get_my_org_workspaces` also require `end_date IS NULL`, so a row with
 * `is_current = true` and an `end_date` counts here but not there. No such row
 * exists today (the app deletes a removed member's row); see #962.
 *
 * The user can read their own rows: the "Org members can view their org
 * members" SELECT policy admits every row of an org the caller is a member of
 * (`is_org_member(auth.uid(), org_id)`), which always includes the caller's
 * own row.
 *
 * `hasCurrentMembership` is null until the query has resolved (or while it is
 * disabled). A failed read throws rather than resolving to false, so it is
 * never cached as "not staff"; the caller decides how to treat `isError`.
 */
export const useHasCurrentOrgMembership = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { user, isLoading: authLoading } = useAuth();

  const { data, isError } = useQuery({
    queryKey: [CURRENT_ORG_MEMBERSHIP_QUERY_KEY, user?.id],
    queryFn: async (): Promise<boolean> => {
      // Outside any try: a skipped call must not be cached as "no membership" (#794).
      await requireLiveSession();
      const { data: rows, error } = await supabase
        .from("organization_members")
        .select("id")
        .eq("user_id", user.id)
        .eq("is_current", true)
        .limit(1);
      if (error) {
        console.error("Error checking current org membership:", error.message);
        throw error;
      }
      return (rows ?? []).length > 0;
    },
    enabled: enabled && !authLoading && !!user,
    // Membership changes rarely within a session. Acceptance updates the cache
    // directly (AcceptInvitePage); a removal is picked up on the next refetch.
    staleTime: 1000 * 60 * 5,
  });

  return {
    hasCurrentMembership: data ?? null,
    isError,
  };
};
